import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { AppShell } from '../components/layout/AppShell'
import { useAuth } from '../hooks/useAuth'
import { orgService } from '../services/orgService'
import { getRoutePolyline, PLACEHOLDER_LEGS } from '../utils/legData'
import { VAN_ROUTE_POINTS } from '../utils/vanRouteData'
import { formatTimePT } from '../utils/formatTime'
import { useNightMode } from '../hooks/useNightMode'
import { RaceMultiSelect } from '../components/RaceMultiSelect'

// ── Types ─────────────────────────────────────────────────────────────────────

interface VanDot {
  teamId: number
  teamName: string
  bibNumber: number | null
  division: string | null
  category: string | null
  vanNumber: 1 | 2
  latitude: number
  longitude: number
  timestamp: string
  startTime: string | null
  raceId: number
  raceName: string
}

function vanAgeLabel(timestamp: string): string {
  const ageMs = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(ageMs / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins} min ago`
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`
}

function isVanOnline(timestamp: string): boolean {
  return Date.now() - new Date(timestamp).getTime() <= 5 * 60 * 1000
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function xpIcon(num: number) {
  return L.divIcon({
    className: '',
    html: `<div style="background:#374151;color:white;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5)">${num}</div>`,
    iconAnchor: [13, 13],
    iconSize: [26, 26],
  })
}

function vanIcon(vanNumber: 1 | 2, online: boolean, bib: number | null) {
  const bg = vanNumber === 1 ? (online ? '#1d4ed8' : '#93c5fd') : (online ? '#7e22ce' : '#d8b4fe')
  const label = bib != null ? String(bib) : vanNumber === 1 ? 'V1' : 'V2'
  const fontSize = label.length > 3 ? 7 : label.length > 2 ? 8 : 9
  const opacity = online ? 1 : 0.5
  return L.divIcon({
    className: '',
    html: `<div style="background:${bg};color:white;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:${fontSize}px;font-weight:bold;border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.4);white-space:nowrap;opacity:${opacity}">${label}</div>`,
    iconAnchor: [13, 13],
    iconSize: [26, 26],
  })
}

// ── Weather click handler ─────────────────────────────────────────────────────

interface WeatherPoint {
  lat: number
  lng: number
  temp: number       // °F
  feelsLike: number  // °F
  windSpeed: number  // mph
  windDir: number    // degrees
  description: string
}

function windDirLabel(deg: number): string {
  const dirs = ['N','NE','E','SE','S','SW','W','NW']
  return dirs[Math.round(deg / 45) % 8]
}

function WeatherClickHandler({
  active, owmKey, onResult,
}: {
  active: boolean
  owmKey: string
  onResult: (point: WeatherPoint | null) => void
}) {
  useMapEvents({
    click: async (e) => {
      if (!active || !owmKey) return
      const { lat, lng } = e.latlng
      try {
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${owmKey}&units=imperial`
        )
        if (!res.ok) return
        const d = await res.json()
        onResult({
          lat, lng,
          temp: Math.round(d.main.temp),
          feelsLike: Math.round(d.main.feels_like),
          windSpeed: Math.round(d.wind.speed),
          windDir: d.wind.deg ?? 0,
          description: d.weather?.[0]?.description ?? '',
        })
      } catch { /* silent */ }
    },
  })
  return null
}

// ── Fit bounds on load ────────────────────────────────────────────────────────

function FitCourse({ points }: { points: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (points.length < 2) return
    map.invalidateSize()
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30] })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

// ── Weather types ─────────────────────────────────────────────────────────────

interface NwsAlert {
  id: string
  event: string
  headline: string
  severity: string
  urgency: string
  onset: string | null
  expires: string | null
}

// ── RainViewer tile path (fetched once, refreshed every 10 min) ───────────────

async function fetchRainViewerPath(): Promise<string | null> {
  try {
    const res = await fetch('https://api.rainviewer.com/public/weather-maps.json')
    const data = await res.json()
    const frames: any[] = data?.radar?.past ?? []
    if (frames.length === 0) return null
    const latest = frames[frames.length - 1]
    return `${data.host}${latest.path}`
  } catch { return null }
}

// ── Main page ─────────────────────────────────────────────────────────────────

// Paste your free OpenWeatherMap API key here once you have it:
const OWM_KEY = '009f5d75cf29caa2b79b02ef764f8824'


export function OrgMapPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [races, setRaces] = useState<Array<{ id: number; name: string; timezone: string }>>([])
  const [selectedRaceIds, setSelectedRaceIds] = useState<Set<number>>(new Set())
  const [vans, setVans] = useState<VanDot[]>([])
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showOffline, setShowOffline] = useState(true)
  const [panelOpen, setPanelOpen] = useState(true)
  const [vanRouteMode, setVanRouteMode] = useState<'off' | 'on'>('off')
  const { nightMode, toggleNightMode } = useNightMode()
  const [simClock, setSimClock] = useState<{ simTime: string; multiplier: number } | null>(null)

  // Weather layer toggles
  const [weatherOpen, setWeatherOpen] = useState(false)
  const [showRadar, setShowRadar] = useState(false)
  const [showWind, setShowWind] = useState(false)
  const [showTemp, setShowTemp] = useState(false)
  const [rainViewerPath, setRainViewerPath] = useState<string | null>(null)
  const [nwsAlerts, setNwsAlerts] = useState<NwsAlert[]>([])
  const [weatherPoint, setWeatherPoint] = useState<WeatherPoint | null>(null)

  // Fetch RainViewer tile path when radar is enabled, refresh every 10 min
  useEffect(() => {
    if (!showRadar) return
    fetchRainViewerPath().then(setRainViewerPath)
    const id = setInterval(() => fetchRainViewerPath().then(setRainViewerPath), 10 * 60 * 1000)
    return () => clearInterval(id)
  }, [showRadar])

  // Poll NWS alerts every 5 min — Oregon (OR) covers RTO course
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await fetch('https://api.weather.gov/alerts/active?area=OR&status=actual&message_type=alert')
        if (!res.ok) return
        const data = await res.json()
        const relevant = (data.features ?? [])
          .filter((f: any) => {
            const ev: string = f.properties?.event ?? ''
            return ev.includes('Thunderstorm') || ev.includes('Tornado') || ev.includes('Lightning') || ev.includes('Severe')
          })
          .map((f: any) => ({
            id: f.properties.id,
            event: f.properties.event,
            headline: f.properties.headline ?? f.properties.event,
            severity: f.properties.severity,
            urgency: f.properties.urgency,
            onset: f.properties.onset ?? null,
            expires: f.properties.expires ?? null,
          }))
        setNwsAlerts(relevant)
      } catch { /* silent — don't disrupt map on network error */ }
    }
    fetchAlerts()
    const id = setInterval(fetchAlerts, 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [])

  // Poll sim clock every 5s
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch('/api/sim/clock')
        if (!res.ok) return
        const data = await res.json()
        if (data.running && data.simTime) {
          setSimClock({ simTime: data.simTime, multiplier: data.multiplier })
        } else {
          setSimClock(null)
        }
      } catch { setSimClock(null) }
    }
    poll()
    const id = setInterval(poll, 5000)
    return () => clearInterval(id)
  }, [])

  const coursePoints = getRoutePolyline()
  const multiRace = selectedRaceIds.size > 1
  const tzForRace = (raceId: number) => races.find(r => r.id === raceId)?.timezone ?? 'America/Los_Angeles'
  const primaryTz = races.find(r => selectedRaceIds.has(r.id))?.timezone ?? 'America/Los_Angeles'

  // Load races — restore multi-selection from localStorage, fall back to all active
  useEffect(() => {
    orgService.getRaces().then(all => {
      const raceList = all.map(r => ({ id: r.id, name: r.name, timezone: r.timezone ?? 'America/Los_Angeles' }))
      setRaces(raceList)
      const saved = localStorage.getItem('org_selected_race_ids')
      let ids: Set<number>
      if (saved) {
        try {
          const parsed: number[] = JSON.parse(saved)
          const valid = parsed.filter(id => all.some(r => r.id === id))
          ids = valid.length > 0 ? new Set(valid) : new Set(all.map(r => r.id))
        } catch {
          ids = new Set(all.map(r => r.id))
        }
      } else {
        // Legacy single-race key migration
        const legacy = localStorage.getItem('org_selected_race_id')
        const legacyId = legacy ? parseInt(legacy, 10) : null
        const match = legacyId ? all.find(r => r.id === legacyId) : null
        ids = match ? new Set([match.id]) : new Set(all.map(r => r.id))
      }
      setSelectedRaceIds(ids)
      if (ids.size === 0) setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const loadAll = async (ids: Set<number>, raceList: Array<{ id: number; name: string; timezone: string }>) => {
    try {
      const results = await Promise.all(
        Array.from(ids).map(async id => {
          const data = await orgService.getVanLocations(id)
          const r = raceList.find(r => r.id === id)
          const name = r?.name ?? ''
          return (data as VanDot[]).map(v => ({ ...v, raceId: id, raceName: name }))
        })
      )
      setVans(results.flat())
      setLastRefresh(new Date())
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedRaceIds.size === 0 || races.length === 0) return
    loadAll(selectedRaceIds, races)
    const interval = setInterval(() => loadAll(selectedRaceIds, races), 30000)
    return () => clearInterval(interval)
  }, [selectedRaceIds, races])

  const handleRaceSelectionChange = (ids: Set<number>) => {
    setSelectedRaceIds(ids)
    localStorage.setItem('org_selected_race_ids', JSON.stringify(Array.from(ids)))
  }

  // Filter — always show all vans (last known location always displayed), but can hide stale by toggle
  const filtered = vans.filter(v => {
    if (!showOffline && !isVanOnline(v.timestamp)) return false
    if (search) {
      const q = search.toLowerCase()
      if (!v.teamName.toLowerCase().includes(q) &&
          !String(v.bibNumber ?? '').includes(q) &&
          !(multiRace && v.raceName.toLowerCase().includes(q))) return false
    }
    return true
  })

  // Counts
  const onlineTeams = new Set(vans.filter(v => isVanOnline(v.timestamp)).map(v => v.teamId)).size
  const offlineTeams = new Set(vans.filter(v => !isVanOnline(v.timestamp)).map(v => v.teamId)).size
  const totalDots = filtered.length

  // Group filtered vans by team for the side panel, nested under race when multi-race
  type TeamGroup = { teamName: string; bibNumber: number | null; raceName: string; vans: VanDot[] }
  const teamGroups = filtered.reduce((acc, v) => {
    const key = `${v.raceId}:${v.teamId}`
    if (!acc[key]) acc[key] = { teamName: v.teamName, bibNumber: v.bibNumber, raceName: v.raceName, vans: [] }
    acc[key].vans.push(v)
    return acc
  }, {} as Record<string, TeamGroup>)

  // For multi-race side panel: group teams under each race
  const raceGroups = multiRace
    ? Array.from(selectedRaceIds).map(raceId => ({
        raceId,
        raceName: races.find(r => r.id === raceId)?.name ?? '',
        teams: Object.entries(teamGroups)
          .filter(([key]) => key.startsWith(`${raceId}:`))
          .map(([, g]) => g)
          .sort((a, b) => (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)),
      }))
    : null

  return (
    <AppShell>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative z-[2000] px-4 py-2 flex-shrink-0 border-b border-gray-200 bg-white flex items-center gap-3">
          {/* Title */}
          <h1 className="text-base font-bold flex items-center gap-1.5 flex-shrink-0">
            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Race Map
          </h1>

          <RaceMultiSelect
            races={races}
            selectedIds={selectedRaceIds}
            onChange={handleRaceSelectionChange}
          />

          <div className="w-px h-4 bg-gray-300 flex-shrink-0" />

          {/* Sim clock */}
          {simClock && (
            <>
              <span className="text-xs font-mono font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded flex-shrink-0">
                SIM {new Date(simClock.simTime).toLocaleString('en-US', {
                  timeZone: primaryTz,
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit', hour12: true
                })} · {simClock.multiplier}x
              </span>
              <div className="w-px h-4 bg-gray-300 flex-shrink-0" />
            </>
          )}

          {/* Status badges */}
          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold flex-shrink-0">{onlineTeams} online</span>
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-semibold flex-shrink-0">{offlineTeams} offline</span>
          <span className="text-xs text-gray-400 flex-shrink-0">{totalDots} vans</span>

          <div className="w-px h-4 bg-gray-300 flex-shrink-0" />

          {/* Controls */}
          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={showOffline} onChange={e => setShowOffline(e.target.checked)} className="w-3 h-3" />
            Show offline
          </label>
          <button
            onClick={() => setPanelOpen(o => !o)}
            className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-600 hover:bg-gray-50 flex-shrink-0"
          >
            {panelOpen ? 'Hide list' : 'Show list'}
          </button>
          {lastRefresh && <span className="text-xs text-gray-400 flex-shrink-0 font-mono">{lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
          <button onClick={() => loadAll(selectedRaceIds, races)} className="text-xs text-indigo-600 hover:underline flex-shrink-0">Refresh</button>

          {/* Log Out — far right */}
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            <div className="w-px h-4 bg-gray-300" />
            <button
              onClick={async () => { await logout(); navigate('/login', { replace: true }) }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-red-600 transition-colors text-xs font-medium"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log Out
            </button>
          </div>
        </div>

        {/* NWS alert banner */}
        {nwsAlerts.length > 0 && (
          <div className="flex-shrink-0 bg-red-600 text-white px-4 py-1.5 flex items-center gap-2 text-xs font-semibold">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span>⚡ NWS ALERT: {nwsAlerts.map(a => a.event).join(' · ')}</span>
            {nwsAlerts[0].expires && (
              <span className="font-normal opacity-80 ml-1">
                until {new Date(nwsAlerts[0].expires).toLocaleTimeString('en-US', { timeZone: primaryTz, hour: 'numeric', minute: '2-digit', hour12: true })}
              </span>
            )}
          </div>
        )}

        {/* Body: optional side panel + map */}
        <div className="flex-1 min-h-0 flex overflow-hidden">

          {/* Side panel */}
          {panelOpen && (
            <div className="w-48 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
              <div className="px-2 py-1.5 border-b border-gray-100 flex-shrink-0">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div className="flex-1 overflow-y-auto">
                {multiRace && raceGroups ? (
                  raceGroups.map(rg => rg.teams.length === 0 ? null : (
                    <div key={rg.raceId}>
                      <div className="px-2 py-1 bg-indigo-50 border-b border-indigo-100 sticky top-0">
                        <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide truncate block">{rg.raceName}</span>
                      </div>
                      {rg.teams.map((group, i) => (
                        <div key={i} className="px-2 py-1.5 border-b border-gray-100">
                          <div className="flex items-center gap-1 mb-0.5">
                            {group.bibNumber != null && (
                              <span className="text-xs font-mono font-bold text-gray-500">#{group.bibNumber}</span>
                            )}
                            <span className="text-xs font-semibold text-gray-800 truncate">{group.teamName}</span>
                          </div>
                          <div className="flex gap-1.5">
                            {group.vans.map(v => (
                              <span key={v.vanNumber} className={`text-xs flex items-center gap-0.5 ${isVanOnline(v.timestamp) ? 'text-blue-600' : 'text-gray-400'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${v.vanNumber === 1 ? 'bg-blue-600' : 'bg-purple-600'} ${!isVanOnline(v.timestamp) ? 'opacity-40' : ''}`} />
                                V{v.vanNumber}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  Object.entries(teamGroups)
                    .sort(([, a], [, b]) => (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999))
                    .map(([key, group]) => (
                      <div key={key} className="px-2 py-1.5 border-b border-gray-100">
                        <div className="flex items-center gap-1 mb-0.5">
                          {group.bibNumber != null && (
                            <span className="text-xs font-mono font-bold text-gray-500">#{group.bibNumber}</span>
                          )}
                          <span className="text-xs font-semibold text-gray-800 truncate">{group.teamName}</span>
                        </div>
                        <div className="flex gap-1.5">
                          {group.vans.map(v => (
                            <span key={v.vanNumber} className={`text-xs flex items-center gap-0.5 ${isVanOnline(v.timestamp) ? 'text-blue-600' : 'text-gray-400'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${v.vanNumber === 1 ? 'bg-blue-600' : 'bg-purple-600'} ${!isVanOnline(v.timestamp) ? 'opacity-40' : ''}`} />
                              V{v.vanNumber}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {/* Map */}
          <div className="flex-1 min-w-0 relative">
            <MapContainer
              center={[39.2, -119.9]}
              zoom={9}
              style={{ height: '100%', width: '100%' }}
            >
              {nightMode ? (
                <TileLayer
                  key="dark"
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
                  maxNativeZoom={19} maxZoom={19} zIndex={1}
                />
              ) : (
                <TileLayer
                  key="light"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  maxNativeZoom={19} maxZoom={19} zIndex={1}
                />
              )}
              {/* Weather tile layers */}
              {showRadar && rainViewerPath && (
                <TileLayer
                  key={rainViewerPath}
                  url={`${rainViewerPath}/256/{z}/{x}/{y}/2/1_1.png`}
                  maxNativeZoom={6} tileSize={256}
                  attribution='Weather data by <a href="https://www.rainviewer.com">RainViewer</a>'
                  opacity={0.6}
                  zIndex={200}
                />
              )}
              {showWind && OWM_KEY && (
                <TileLayer
                  url={`https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid=${OWM_KEY}`}
                  attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
                  opacity={0.7}
                  zIndex={201}
                />
              )}
              {showTemp && OWM_KEY && (
                <TileLayer
                  url={`https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=${OWM_KEY}`}
                  attribution='&copy; <a href="https://openweathermap.org">OpenWeatherMap</a>'
                  opacity={0.5}
                  zIndex={202}
                />
              )}

              <FitCourse points={coursePoints} />
              <WeatherClickHandler
                active={showWind || showTemp}
                owmKey={OWM_KEY}
                onResult={setWeatherPoint}
              />
              {weatherPoint && (
                <Marker
                  position={[weatherPoint.lat, weatherPoint.lng]}
                  icon={L.divIcon({
                    className: '',
                    html: `<div style="background:#1e3a5f;color:white;border-radius:6px;padding:3px 7px;font-size:10px;font-weight:bold;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.3);display:inline-block">${weatherPoint.temp}°F · ${weatherPoint.windSpeed}mph ${windDirLabel(weatherPoint.windDir)}</div>`,
                    iconAnchor: [0, 0],
                    iconSize: [0, 0],
                  })}
                >
                  <Popup eventHandlers={{ remove: () => setWeatherPoint(null) }}>
                    <div className="text-xs space-y-1 min-w-[140px]">
                      <p className="font-bold text-gray-800 capitalize">{weatherPoint.description}</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-gray-600">
                        <span>Temp</span><span className="font-mono font-semibold">{weatherPoint.temp}°F</span>
                        <span>Feels like</span><span className="font-mono font-semibold">{weatherPoint.feelsLike}°F</span>
                        <span>Wind</span><span className="font-mono font-semibold">{weatherPoint.windSpeed} mph {windDirLabel(weatherPoint.windDir)}</span>
                      </div>
                      <p className="text-[10px] text-gray-400">{weatherPoint.lat.toFixed(4)}, {weatherPoint.lng.toFixed(4)}</p>
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Full course route */}
              <Polyline positions={coursePoints} color="#1e40af" weight={3} opacity={0.7} dashArray="8 5" />

              {/* Van route */}
              {vanRouteMode === 'on' && (
                <Polyline positions={VAN_ROUTE_POINTS} color="#7e22ce" weight={3} opacity={0.9} />
              )}

              {/* Exchange point markers */}
              {PLACEHOLDER_LEGS.map(leg => (
                <Marker
                  key={`xp-${leg.legNumber}`}
                  position={[leg.startLat, leg.startLng]}
                  icon={xpIcon(leg.legNumber)}
                >
                  <Popup>
                    <div className="text-xs">
                      <p className="font-bold">XP {leg.legNumber}</p>
                      <p className="text-gray-500">{leg.startAddress}</p>
                      <p className="text-gray-400">{leg.startLat.toFixed(4)}, {leg.startLng.toFixed(4)}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Finish marker */}
              {PLACEHOLDER_LEGS.length > 0 && (() => {
                const last = PLACEHOLDER_LEGS[PLACEHOLDER_LEGS.length - 1]
                return (
                  <Marker position={[last.endLat, last.endLng]} icon={L.divIcon({
                    className: '',
                    html: `<div style="background:#1d4ed8;color:white;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:bold;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5)">★</div>`,
                    iconAnchor: [10, 10], iconSize: [20, 20],
                  })}>
                    <Popup><div className="text-xs font-bold">Finish Line</div></Popup>
                  </Marker>
                )
              })()}

              {/* Van dots — always shown, faded when stale */}
              {filtered.map(v => {
                const online = isVanOnline(v.timestamp)
                return (
                  <Marker
                    key={`${v.teamId}-${v.vanNumber}`}
                    position={[v.latitude, v.longitude]}
                    icon={vanIcon(v.vanNumber as 1 | 2, online, v.bibNumber)}
                  >
                    <Popup>
                      <div className="text-xs space-y-1 min-w-[150px]">
                        <div className="flex items-center gap-1.5 pb-1 border-b border-gray-200 flex-wrap">
                          {v.bibNumber != null && <span className="font-mono font-bold text-gray-500">#{v.bibNumber}</span>}
                          <span className="font-bold text-gray-900">{v.teamName}</span>
                        </div>
                        {multiRace && (
                          <p className="text-indigo-600 font-semibold text-[10px]">{v.raceName}</p>
                        )}
                        {(v.division || v.category) && (
                          <p className="text-gray-500">
                            {[v.division, v.category].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <p className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${v.vanNumber === 1 ? 'bg-blue-600' : 'bg-purple-600'}`} />
                          <span className="font-semibold">Van {v.vanNumber}</span>
                          <span className={`ml-auto text-xs font-semibold ${online ? 'text-green-600' : 'text-gray-400'}`}>
                            {online ? '● Live' : '○ ' + vanAgeLabel(v.timestamp)}
                          </span>
                        </p>
                        <p className="text-gray-500">{formatTimePT(new Date(v.timestamp))}</p>
                        {v.startTime && (
                          <p className="text-gray-500">
                            Start: <span className="font-mono">{new Date(v.startTime).toLocaleTimeString('en-US', { timeZone: tzForRace(v.raceId), hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )
              })}
            </MapContainer>

            {/* Right-side controls — bottom right, above nav bar */}
            <div className="absolute bottom-24 right-3 z-[1000] flex flex-col gap-2 items-end">

              {/* Weather group box */}
              <div className="bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden w-36">
                <button
                  onClick={() => setWeatherOpen(o => !o)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold transition-colors ${
                    (showRadar || showWind || showTemp) ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                    Weather
                  </span>
                  <span className="text-gray-400">{weatherOpen ? '▲' : '▼'}</span>
                </button>
                {weatherOpen && (
                  <div className="border-t border-gray-200 px-3 py-2 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={showRadar} onChange={e => setShowRadar(e.target.checked)} className="w-3 h-3 accent-blue-600" />
                      <span className="text-xs text-gray-700">🌧 Radar</span>
                    </label>
                    <label className={`flex items-center gap-2 ${OWM_KEY ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                      <input type="checkbox" checked={showWind} onChange={e => OWM_KEY && setShowWind(e.target.checked)} disabled={!OWM_KEY} className="w-3 h-3 accent-blue-600" />
                      <span className="text-xs text-gray-700">💨 Wind</span>
                    </label>
                    <label className={`flex items-center gap-2 ${OWM_KEY ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                      <input type="checkbox" checked={showTemp} onChange={e => OWM_KEY && setShowTemp(e.target.checked)} disabled={!OWM_KEY} className="w-3 h-3 accent-blue-600" />
                      <span className="text-xs text-gray-700">🌡 Temp</span>
                    </label>
                    {!OWM_KEY && (
                      <p className="text-[10px] text-gray-400 leading-tight">Add OWM key for wind &amp; temp</p>
                    )}
                    <div className="pt-1 border-t border-gray-100">
                      <p className="text-[10px] text-gray-400 leading-tight flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${nwsAlerts.length > 0 ? 'bg-red-500' : 'bg-green-400'}`} />
                        {nwsAlerts.length > 0 ? `${nwsAlerts.length} NWS alert${nwsAlerts.length > 1 ? 's' : ''}` : 'No NWS alerts'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Night mode */}
              <button
                onClick={toggleNightMode}
                className={`flex items-center justify-center gap-1.5 w-36 py-2 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
                  nightMode ? 'bg-indigo-900 text-indigo-100 border-indigo-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {nightMode ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-9H21M3 12H2m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
                {nightMode ? 'Day Mode' : 'Night Mode'}
              </button>

              {/* Van route */}
              <button
                onClick={() => setVanRouteMode(m => m === 'off' ? 'on' : 'off')}
                className={`flex items-center justify-center gap-1.5 w-36 py-2 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
                  vanRouteMode === 'on' ? 'bg-purple-800 text-white border-purple-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="5" cy="5" r="2" /><circle cx="19" cy="19" r="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 7v6h14v4" />
                </svg>
                {vanRouteMode === 'on' ? 'Van: On' : 'Van Route'}
              </button>
            </div>

            {/* Legend — above nav bar */}
            <div className="absolute bottom-24 left-3 z-[1000] bg-white rounded-lg shadow border border-gray-200 px-3 py-2 text-xs space-y-1">
              <p className="font-semibold text-gray-600 text-xs mb-1">Legend</p>
              <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-blue-700 border-2 border-white shadow" /><span>Van 1 (online)</span></div>
              <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-blue-200 border-2 border-white shadow" /><span>Van 1 (offline)</span></div>
              <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-purple-700 border-2 border-white shadow" /><span>Van 2 (online)</span></div>
              <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-purple-200 border-2 border-white shadow" /><span>Van 2 (offline)</span></div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
